import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ScenarioCapTableByStakeholder } from '../../../../../types/scenario-modeling.types';
import { toNumber } from '../../../../../utils/to-number.util';
import { CapTableTab, Column, StakeholderColumns, StakeholdersViewBy } from '../fundraising.types';
import { TableRow } from '../table-components/index';

export type StakeholderTableItemProps = {
  className?: string;
  fields: Record<
    StakeholderColumns,
    {
      change: null | number | number[] | string | string[];
      value: number | number[] | string | string[];
    } | null
  >;
  items?: ScenarioCapTableByStakeholder[];
  modifiedShareClassesIds?: string[];
  selectedViewBy: 'all' | StakeholdersViewBy;
  trClassName?: string;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export const StakeholderTableItem: FC<StakeholderTableItemProps> = ({
  fields,
  visibleColumns,
  className,
  trClassName,
  viewBy,
  selectedViewBy,
  items = [],
  modifiedShareClassesIds = [],
}) => {
  const [isExpanded, setExpanded] = useState(false);

  const isItemsEmpty = items.length === 0;

  return (
    <>
      <TableRow
        fields={fields}
        handleExpandRow={setExpanded}
        isExpanded={isExpanded}
        isExpandedRow={isItemsEmpty}
        selectedViewBy={selectedViewBy}
        tab={CapTableTab.BY_STAKEHOLDER}
        tdClassName={className}
        trClassName={twMerge(trClassName, isExpanded && 'bg-brand-25')}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      {isExpanded &&
        items.map(
          (
            {
              avgSharePrice,
              convertedBasis,
              convertedBasisPercentage,
              diluted,
              dilutedPercentage,
              investment,
              issued,
              issuedPercentage,
              votingPercentage,
              shareClasses,
              name,
              avgSharePriceChange,
              convertedBasisChange,
              convertedBasisPercentageChange,
              dilutedChange,
              dilutedPercentageChange,
              investmentChange,
              issuedChange,
              issuedPercentageChange,
              voting,
              votingChange,
              votingPercentageChange,
            },
            i,
          ) => {
            const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
            const otherShareClassWithIssued = shareClasses.filter(
              ({ issued, isDefault }) => issued > 0 && !isDefault,
            );

            const hasModified = otherShareClassWithIssued.some(({ shareClass }) =>
              modifiedShareClassesIds.includes(shareClass?.id),
            );

            return (
              <TableRow
                className="bg-[#F9FAFB] shadow-[inset_2px_0px_0px_0px_#2565C8]"
                fields={{
                  [Column.NAME]: { value: name, change: null },
                  [Column.STAKEHOLDER_NAME]: { value: name, change: null },
                  [Column.TYPE]: {
                    value: '-',
                    change: null,
                  },
                  [Column.ISSUED]: {
                    change: issuedPercentageChange,
                    value: issuedPercentage * 100,
                  },
                  [Column.DILUTED]: {
                    value: dilutedPercentage * 100,
                    change: dilutedPercentageChange,
                  },
                  [Column.VOTING]: {
                    value: votingPercentage * 100,
                    change: votingPercentageChange,
                  },
                  [Column.CONVERTED_PERCENTAGE]: {
                    value: convertedBasisPercentage * 100,
                    change: convertedBasisPercentageChange,
                  },
                  [Column.NO_ISSUED]: {
                    change: issuedChange,
                    value: issued,
                  },
                  [Column.DILUTED_SHARES]: {
                    change: dilutedChange,
                    value: diluted,
                  },
                  [Column.COMMON_STOCK]: {
                    change: toNumber(defaultShareClass?.issuedChange),
                    value: toNumber(defaultShareClass?.issued),
                  },
                  [Column.OTHER_SHARE_CLASSES]: {
                    value: shareClasses
                      .filter(({ isDefault }) => !isDefault)
                      .map(({ issued }) => issued),
                    change: shareClasses
                      .filter(({ isDefault }) => !isDefault)
                      .map(({ issuedChange }) => issuedChange),
                  },
                  [Column.VOTING_RIGHTS]: {
                    change: votingChange,
                    value: voting,
                  },
                  [Column.INVESTMENT]: {
                    change: investmentChange,
                    value: investment,
                  },
                  [Column.AVERAGE_SHARE_PRICE]: {
                    change: avgSharePriceChange,
                    value: avgSharePrice,
                  },
                  [Column.CONVERTED_SHARES]: {
                    change: convertedBasisChange,
                    value: convertedBasis,
                  },
                }}
                isExpandedRow={true}
                key={`${i}_${name}`}
                selectedViewBy={selectedViewBy}
                showToolTip={hasModified}
                tab={CapTableTab.BY_STAKEHOLDER}
                trClassName="bg-brand-25"
                viewBy={viewBy}
                visibleColumns={visibleColumns}
              />
            );
          },
        )}
    </>
  );
};
